import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChangePasswordModal } from "@/components/app/ChangePasswordModal";
import { Plus, Trash2, ShieldCheck, Pencil, X, User, Phone, Lock, KeyRound } from "lucide-react";

const AdminEditModal = ({ admin, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (admin) {
      setName(admin.name || "");
      setPhone(admin.phone || "");
      setPassword("");
    }
  }, [admin]);

  if (!admin) return null;

  const save = async (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length !== 10) {
      toast.error("Telefon numarası 10 haneli olmalıdır");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admins/${admin.id}`, { name, phone, password: password || undefined });
      toast.success("Kullanıcı güncellendi");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Güncellenemedi");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4" data-testid="admin-edit-modal">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[var(--fb-border)] p-6 animate-fade-up">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-extrabold text-xl">Kullanıcıyı Düzenle</h2>
          <button data-testid="admin-edit-close" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={save} className="space-y-3">
          <div className="relative">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
            <input
              data-testid="admin-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ad Soyad"
              className="w-full border border-[var(--fb-border)] rounded-xl pl-9 pr-3 py-3 outline-none focus:border-[var(--fb-red)]"
            />
          </div>
          <div className="relative">
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
            <input
              data-testid="admin-edit-phone"
              value={phone}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="Telefon (10 haneli)"
              className="w-full border border-[var(--fb-border)] rounded-xl pl-9 pr-3 py-3 outline-none focus:border-[var(--fb-red)]"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
            <input
              data-testid="admin-edit-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Yeni şifre (boş bırakırsanız değişmez)"
              className="w-full border border-[var(--fb-border)] rounded-xl pl-9 pr-3 py-3 outline-none focus:border-[var(--fb-red)]"
            />
          </div>
          <button
            data-testid="admin-edit-submit"
            type="submit"
            disabled={saving}
            className="w-full bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors active:scale-[0.99]"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </form>
      </div>
    </div>
  );
};

export const AdminsPanel = ({ currentUser }) => {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/admins");
      setAdmins(res.data);
    } catch {
      toast.error("Kullanıcılar yüklenemedi");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!form.name.trim() || !form.phone.trim() || form.password.length < 4) {
      toast.error("Ad, telefon ve en az 4 karakter şifre gerekli");
      return;
    }
    if (form.phone.replace(/\D/g, "").length !== 10) {
      toast.error("Telefon numarası 10 haneli olmalıdır");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admins", form);
      setForm({ name: "", phone: "", password: "" });
      load();
      toast.success("Kullanıcı eklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Bu yönetici kullanıcısını silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/admins/${id}`);
      load();
      toast.success("Kullanıcı silindi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="max-w-2xl" data-testid="admins-panel">
      {/* Şifre değiştir */}
      <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-4 mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="font-display font-bold">Şifre Değiştir</p>
          <p className="text-xs text-[var(--fb-muted)]">Kendi giriş şifrenizi güncelleyin</p>
        </div>
        <button
          data-testid="change-password-btn"
          onClick={() => setPwOpen(true)}
          className="flex items-center gap-1.5 text-sm font-semibold text-[var(--fb-red)] border border-[var(--fb-red)] hover:bg-red-50 px-4 py-2 rounded-full transition-colors shrink-0"
        >
          <KeyRound className="w-4 h-4" /> Şifre Değiştir
        </button>
      </div>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-4 mb-4">
        <p className="font-display font-bold mb-3">Yeni Yönetici Ekle</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            data-testid="admin-form-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ad Soyad"
            className="border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
          <input
            data-testid="admin-form-phone"
            value={form.phone}
            inputMode="numeric"
            maxLength={10}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
            placeholder="Telefon (10 haneli)"
            className="border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
          <input
            data-testid="admin-form-password"
            type="password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Şifre (min 4)"
            className="border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
        </div>
        <button
          data-testid="add-admin-btn"
          onClick={add}
          disabled={saving}
          className="mt-3 flex items-center gap-1 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-full transition-colors active:scale-95"
        >
          <Plus className="w-4 h-4" /> Ekle
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[var(--fb-border)] overflow-hidden divide-y divide-[var(--fb-border)]">
        {admins.map((u) => (
          <div key={u.id} data-testid={`admin-row-${u.id}`} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-[var(--fb-offwhite)]">
            <div className="w-10 h-10 rounded-full grid place-items-center shrink-0 bg-red-50 text-[var(--fb-red)]">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">
                {u.name || "İsimsiz"}
                {currentUser?.id === u.id && <span className="text-xs text-[var(--fb-muted)] font-normal"> (siz)</span>}
              </p>
              <p className="text-xs text-[var(--fb-muted)]">{u.phone} · Yönetici</p>
            </div>
            <button
              data-testid={`edit-admin-${u.id}`}
              onClick={() => setEditing(u)}
              className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] text-[var(--fb-muted)]"
            >
              <Pencil className="w-4 h-4" />
            </button>
            {currentUser?.id !== u.id && (
              <button
                data-testid={`delete-admin-${u.id}`}
                onClick={() => del(u.id)}
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-red-50 text-[var(--fb-red)]"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <AdminEditModal admin={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
};
